/**
 * Client-side audio duration resolution for the upload paths.
 *
 * Every audio upload has to send `audioDurationSec` with its presign request -
 * that field is the only thing the server's duration ceiling can read, and the
 * player/waveform UI shows the same number. The obvious implementation (load
 * the blob into an `<audio>` and read `duration`) is wrong for exactly the
 * files we produce most: `MediaRecorder` writes a webm (and, on Safari, an
 * mp4) with no duration in the container header, so `HTMLMediaElement.duration`
 * comes back `0` or `Infinity` for every recording made in the app.
 *
 * Three sources, cheapest and most accurate first:
 *
 * 1. The recorder's own wall clock. It measured the take; nothing else needs to
 *    guess. `recorder-store` publishes each finished clip here as it produces
 *    it, keyed by byte size + MIME so the `File` the composer wraps the blob in
 *    still matches.
 * 2. Metadata probe with the seek-past-the-end trick. For headerless webm/mp4
 *    the browser only learns the real end time if you force it to seek there,
 *    at which point it reports it on `durationchange`.
 * 3. Full decode. Exact, but it materializes PCM (roughly 190x an opus stream),
 *    so it is size-gated and only ever runs when both cheaper reads failed.
 */

/** Last N finished recordings kept for matching. Covers a re-send of an earlier take. */
const RECORDED_CLIP_HISTORY = 4;

/**
 * `currentTime` value used to force a seek past the end of a headerless
 * stream. Any absurd number works; this is the value the workaround is
 * usually written with.
 */
const SEEK_PAST_END_SECONDS = 1e101;

/** Give up on the metadata probe rather than block a send behind a wedged element. */
const METADATA_PROBE_TIMEOUT_MS = 5_000;

/**
 * Decoding allocates float32 PCM for the whole file. 10MB of opus is already
 * ~2.5GB of PCM at 48kHz stereo, so anything larger falls through to "unknown"
 * instead of taking the tab down. Long recordings come from the recorder and
 * are answered by source 1 anyway.
 */
const DECODE_FALLBACK_MAX_BYTES = 10 * 1024 * 1024;

interface RecordedClip {
	size: number;
	mimeType: string;
	durationSec: number;
}

let recordedClips: RecordedClip[] = [];

/** `audio/webm;codecs=opus` and `audio/webm` are the same container. */
const baseMimeType = (mimeType: string): string =>
	mimeType.split(';')[0]?.trim().toLowerCase() ?? '';

/**
 * Publish a finished recording's measured length. Called by the recorder as it
 * finalizes a blob - the composer then wraps that blob in a `File`, which loses
 * the object identity but keeps the byte size and MIME type, so those are the
 * match key.
 */
export const rememberRecordedClip = (blob: Blob, durationSec: number): void => {
	if (!Number.isFinite(durationSec) || durationSec <= 0) return;
	recordedClips = [
		{ size: blob.size, mimeType: baseMimeType(blob.type), durationSec },
		...recordedClips.filter((clip) => clip.size !== blob.size),
	].slice(0, RECORDED_CLIP_HISTORY);
};

/** The recorder's own measurement for this blob, when it made it. */
export const recordedClipDurationSec = (file: Blob): number | undefined =>
	recordedClips.find(
		(clip) => clip.size === file.size && clip.mimeType === baseMimeType(file.type),
	)?.durationSec;

const getAudioContextCtor = (): typeof AudioContext | null => {
	if (typeof window === 'undefined') return null;
	const w = window as unknown as {
		AudioContext?: typeof AudioContext;
		webkitAudioContext?: typeof AudioContext;
	};
	return w.AudioContext ?? w.webkitAudioContext ?? null;
};

/**
 * Read duration off a media element, seeking past the end when the container
 * carries no duration (every `MediaRecorder` output). Resolves 0 when the file
 * refuses to yield one.
 */
const probeMetadataDurationSec = (file: Blob): Promise<number> =>
	new Promise((resolve) => {
		if (typeof window === 'undefined' || typeof Audio === 'undefined') {
			resolve(0);
			return;
		}

		const url = URL.createObjectURL(file);
		const audio = new Audio();
		let settled = false;
		let timer: ReturnType<typeof setTimeout> | undefined;

		const finish = (value: number) => {
			if (settled) return;
			settled = true;
			if (timer) clearTimeout(timer);
			audio.onloadedmetadata = null;
			audio.ondurationchange = null;
			audio.onerror = null;
			audio.src = '';
			URL.revokeObjectURL(url);
			resolve(Number.isFinite(value) && value > 0 ? value : 0);
		};

		const hasUsableDuration = () => Number.isFinite(audio.duration) && audio.duration > 0;

		audio.preload = 'metadata';
		audio.onloadedmetadata = () => {
			if (hasUsableDuration()) {
				finish(audio.duration);
				return;
			}
			// Headerless stream: force the browser to scan to the real end. It
			// reports the length it found on `durationchange`.
			audio.ondurationchange = () => {
				if (hasUsableDuration()) finish(audio.duration);
			};
			try {
				audio.currentTime = SEEK_PAST_END_SECONDS;
			} catch {
				finish(0);
			}
		};
		audio.onerror = () => finish(0);
		timer = setTimeout(() => finish(audio.duration), METADATA_PROBE_TIMEOUT_MS);
		audio.src = url;
	});

/** Last resort: decode the whole file. Size-gated - see `DECODE_FALLBACK_MAX_BYTES`. */
const decodeDurationSec = async (file: Blob): Promise<number> => {
	if (file.size > DECODE_FALLBACK_MAX_BYTES) return 0;
	const Ctor = getAudioContextCtor();
	if (!Ctor) return 0;
	const ctx = new Ctor();
	try {
		const decoded = await ctx.decodeAudioData(await file.arrayBuffer());
		return Number.isFinite(decoded.duration) ? decoded.duration : 0;
	} catch {
		return 0;
	} finally {
		ctx.close().catch(() => {});
	}
};

/**
 * Duration of an audio file in seconds, or 0 when it genuinely can't be
 * determined. Callers should omit `audioDurationSec` from the presign body on
 * 0 rather than send it - a zero would pass the server's ceiling silently and
 * read as "measured, and short".
 */
export const resolveAudioDurationSec = async (file: File): Promise<number> => {
	const recorded = recordedClipDurationSec(file);
	if (recorded) return recorded;
	try {
		const fromMetadata = await probeMetadataDurationSec(file);
		const seconds = fromMetadata > 0 ? fromMetadata : await decodeDurationSec(file);
		return Math.round(seconds * 10) / 10;
	} catch (err) {
		console.warn('[audio-duration] probe failed', err);
		return 0;
	}
};
